"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCodeGeneration = void 0;
const react_1 = require("react");
const codeGeneration_1 = require("../types/codeGeneration");
const commitUtils_1 = require("../utils/commitUtils");
const generateCode_1 = require("../components/generateCode");
const useCodeGeneration = (options) => {
    const { userId, onStateChange, onError } = options;
    // State
    const [appState, setAppState] = (0, react_1.useState)(codeGeneration_1.AppState.INITIAL);
    const [referenceImages, setReferenceImages] = (0, react_1.useState)({
        image_url: "",
        file_type: "",
    });
    const [head, setHead] = (0, react_1.useState)(null);
    const [commits, setCommits] = (0, react_1.useState)({});
    const [executionConsoles, setExecutionConsoles] = (0, react_1.useState)({});
    const [inputMode, setInputMode] = (0, react_1.useState)("image");
    // Refs
    const wsRef = (0, react_1.useRef)(null);
    // Update app state with callback
    const updateAppState = (0, react_1.useCallback)((newState) => {
        setAppState(newState);
        onStateChange === null || onStateChange === void 0 ? void 0 : onStateChange(newState);
    }, [onStateChange]);
    // Reset execution consoles
    const resetExecutionConsoles = (0, react_1.useCallback)(() => {
        setExecutionConsoles({});
    }, []);
    // Append execution console
    const appendExecutionConsole = (0, react_1.useCallback)((variantIndex, line) => {
        setExecutionConsoles((prev) => (Object.assign(Object.assign({}, prev), { [variantIndex]: [...(prev[variantIndex] || []), line] })));
    }, []);
    // Add commit
    const addCommit = (0, react_1.useCallback)((commit) => {
        setCommits((prev) => (0, commitUtils_1.addNewCommit)(prev, commit));
    }, []);
    // Remove commit
    const removeCommitFromState = (0, react_1.useCallback)((hash) => {
        setCommits((prev) => (0, commitUtils_1.removeCommit)(prev, hash));
    }, []);
    // Reset all state
    const reset = (0, react_1.useCallback)(() => {
        updateAppState(codeGeneration_1.AppState.INITIAL);
        resetExecutionConsoles();
        setCommits({});
        setHead(null);
        setInputMode("image");
        setReferenceImages({
            image_url: "",
            file_type: "",
        });
    }, [updateAppState, resetExecutionConsoles]);
    // Cancel code generation and reset
    const cancelCodeGenerationAndReset = (0, react_1.useCallback)((commit) => {
        if (commit.type === "ai_create") {
            reset();
        }
        else {
            removeCommitFromState(commit.hash);
            const parentCommitHash = commit.parentHash;
            if (parentCommitHash) {
                setHead(parentCommitHash);
            }
            else {
                onError === null || onError === void 0 ? void 0 : onError("Parent commit not found");
                return;
            }
            updateAppState(codeGeneration_1.AppState.CODE_READY);
        }
    }, [reset, removeCommitFromState, updateAppState, onError]);
    // Generate code
    const doGenerateCode = (0, react_1.useCallback)((params) => {
        updateAppState(codeGeneration_1.AppState.CODING);
        const baseCommitObject = {
            variants: [{ code: "" }, { code: "" }],
        };
        const commitInputObject = params.generationType === "create"
            ? Object.assign(Object.assign({}, baseCommitObject), { type: "ai_create", parentHash: null, inputs: { image_url: referenceImages.image_url } }) : Object.assign(Object.assign({}, baseCommitObject), { type: "ai_edit", parentHash: head, inputs: {
                prompt: params.history
                    ? params.history[params.history.length - 1]
                    : "",
            } });
        const commit = (0, commitUtils_1.createCommit)(commitInputObject);
        addCommit(commit);
        setHead(commit.hash);
        (0, generateCode_1.generateCode)(wsRef, params, 
        // On change
        (token, variantIndex) => {
            setCommits((prev) => (0, commitUtils_1.appendCommitCode)(prev, commit.hash, variantIndex, token));
        }, 
        // On set code
        (code, variantIndex) => {
            setCommits((prev) => (0, commitUtils_1.setCommitCode)(prev, commit.hash, variantIndex, code));
        }, 
        // On status update
        (line, variantIndex) => appendExecutionConsole(variantIndex, line), 
        // On cancel
        () => {
            cancelCodeGenerationAndReset(commit);
        }, 
        // On complete
        () => {
            updateAppState(codeGeneration_1.AppState.CODE_READY);
        });
    }, [
        updateAppState,
        referenceImages.image_url,
        head,
        addCommit,
        appendExecutionConsole,
        cancelCodeGenerationAndReset,
    ]);
    // Handle reference images (main function)
    const handleReferenceImages = (0, react_1.useCallback)((image_url, file_type, text) => {
        doGenerateCode({
            generatedCodeConfig: "html_css",
            imageUrl: image_url,
            fileType: file_type,
            userId,
            textInput: text,
        });
    }, [doGenerateCode, userId]);
    // Update reference images
    const updateReferenceImages = (0, react_1.useCallback)((newImages) => {
        setReferenceImages(newImages);
    }, []);
    // Get current commit
    const getCurrentCommit = (0, react_1.useCallback)(() => {
        return head ? commits[head] : null;
    }, [head, commits]);
    // Get current code
    const getCurrentCode = (0, react_1.useCallback)(() => {
        const currentCommit = getCurrentCommit();
        if (!currentCommit)
            return "";
        const selectedVariant = currentCommit.variants[currentCommit.selectedVariantIndex];
        return (selectedVariant === null || selectedVariant === void 0 ? void 0 : selectedVariant.code) || "";
    }, [getCurrentCommit]);
    return {
        // State
        appState,
        referenceImages,
        head,
        commits,
        executionConsoles,
        inputMode,
        wsRef,
        // Actions
        handleReferenceImages,
        updateReferenceImages,
        setInputMode,
        reset,
        cancelCodeGenerationAndReset,
        doGenerateCode,
        // Getters
        getCurrentCommit,
        getCurrentCode,
        // Utilities
        resetExecutionConsoles,
        appendExecutionConsole,
    };
};
exports.useCodeGeneration = useCodeGeneration;
